// WikiChat.jsx — shared chat primitives for the Wiki agents (bubbles, composer, cites).
import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Copy, ArrowUp, ShieldCheck, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { WikiAiAvatar } from './WikiCiteChip';
import WikiCiteChip from './WikiCiteChip';
import { useWikiApi } from '../../hooks/useWiki';

const PALETTE = ['bg-stone-900', 'bg-rose-500', 'bg-emerald-600', 'bg-stone-900', 'bg-amber-500'];
function UserAvatar({ name, size = 28 }) {
    const initials = (name || 'You').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    const color = PALETTE[(name || '').length % PALETTE.length];
    return <div className={`${color} rounded-full text-white font-semibold flex items-center justify-center shrink-0`} style={{ width: size, height: size, fontSize: size * 0.38 }}>{initials}</div>;
}

export function Bubble({ m, userName, onOpenDoc }) {
    const api = useWikiApi();
    const [rated, setRated] = useState(null);
    if (m.role === 'user') {
        return (
            <div className="flex gap-2.5 justify-end">
                <div className="bg-stone-900 text-white rounded-2xl rounded-tr-sm px-3.5 py-2 text-[13px] max-w-[78%] leading-relaxed">{m.text}</div>
                <UserAvatar name={userName} />
            </div>
        );
    }
    const rate = (val) => { setRated(val); if (m.interaction_id) api.feedback(m.interaction_id, { helpful: val }).catch(() => { }); };
    return (
        <div className="flex gap-2.5">
            <WikiAiAvatar />
            <div className="max-w-[82%]">
                {m.thinking ? (
                    <div className="bg-white border border-stone-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5 inline-flex items-center gap-1.5">
                        {[0, 1, 2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-stone-500 wiki-typing-dot" style={{ animationDelay: `${i * 0.15}s` }} />)}
                        <span className="text-[11px] text-stone-500 ml-1">searching {m.scopeLabel || 'pages'}…</span>
                    </div>
                ) : (
                    <div className="bg-white border border-stone-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5">
                        <div className="text-[13px] text-stone-900 leading-relaxed whitespace-pre-line">{m.text}</div>
                        {m.cites && m.cites.length > 0 && (
                            <div className="mt-2.5 pt-2.5 border-t border-stone-200">
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5 flex items-center gap-1"><Link2 className="w-3 h-3" /> Sources</div>
                                <div className="flex flex-wrap gap-1.5">{m.cites.map((c, i) => <WikiCiteChip key={i} cite={c} onOpenDoc={onOpenDoc} />)}</div>
                            </div>
                        )}
                    </div>
                )}
                {!m.thinking && (
                    <div className="flex items-center gap-1 mt-1 ml-1">
                        <button onClick={() => rate(true)} className={`w-6 h-6 rounded flex items-center justify-center ${rated === true ? 'text-emerald-600 bg-emerald-50' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}><ThumbsUp className="w-3 h-3" /></button>
                        <button onClick={() => rate(false)} className={`w-6 h-6 rounded flex items-center justify-center ${rated === false ? 'text-rose-600 bg-rose-50' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}><ThumbsDown className="w-3 h-3" /></button>
                        <button onClick={() => { navigator.clipboard?.writeText(m.text); toast.success('Copied'); }} className="w-6 h-6 rounded text-stone-400 hover:text-stone-600 hover:bg-stone-100 flex items-center justify-center"><Copy className="w-3 h-3" /></button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function Composer({ onSend, placeholder, disabled }) {
    const [v, setV] = useState('');
    const submit = (e) => { e.preventDefault(); if (v.trim() && !disabled) { onSend(v.trim()); setV(''); } };
    return (
        <form onSubmit={submit} className="p-3 border-t border-stone-200 bg-white">
            <div className="flex items-end gap-2 rounded-xl border border-stone-200 focus-within:border-stone-400 focus-within:ring-2 focus-within:ring-stone-600/15 bg-white px-3 py-2">
                <textarea rows={1} value={v} onChange={e => setV(e.target.value)} placeholder={placeholder} disabled={disabled}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) submit(e); }}
                    className="flex-1 resize-none bg-transparent text-[13px] text-stone-900 placeholder:text-stone-400 focus:outline-none leading-relaxed" />
                <button type="submit" disabled={disabled} className="w-8 h-8 rounded-lg wiki-grad text-white flex items-center justify-center wiki-shadow-ai shrink-0 disabled:opacity-50"><ArrowUp className="w-4 h-4" /></button>
            </div>
            <div className="text-[10px] text-stone-400 mt-1.5 px-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Answers are grounded in pages you have permission to read.</div>
        </form>
    );
}

// Shared send logic: appends user + thinking bubbles, calls the API fn, swaps in the answer.
export function useWikiChat(callFn, scopeLabel) {
    const [msgs, setMsgs] = useState([]);
    const send = async (text) => {
        setMsgs(m => [...m, { role: 'user', text }, { role: 'ai', thinking: true, scopeLabel }]);
        try {
            const res = await callFn(text);
            setMsgs(m => [...m.slice(0, -1), { role: 'ai', text: res.answer, cites: res.cites || [], interaction_id: res.interaction_id, status: res.status }]);
        } catch (e) {
            setMsgs(m => [...m.slice(0, -1), { role: 'ai', text: 'Something went wrong reaching the agent. Please try again.', cites: [] }]);
        }
    };
    return { msgs, send, reset: () => setMsgs([]) };
}
